import { CircleCheck, CircleX, CloudUpload, TriangleAlert, type LucideIcon } from 'lucide-react';
import { useRouterState } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { pausarRetorno, retomarRetorno, trocouDeTela, type EfeitoDoRetorno, type Retorno, type TomDoRetorno } from '@/lib/retorno';

/**
 * O tom do retorno (R04, p.32, 1): "verde fez, âmbar fez com ressalva,
 * vermelho não fez, azul em andamento". Um lugar só, para a mesa e o celular
 * desenharem a mesma coisa com o mesmo ícone (p.19: um conceito, um ícone).
 *
 * As classes são tokens do `tokens.css` (nenhuma cor escrita à mão).
 */
export const TOM_DO_RETORNO: Readonly<
  Record<
    TomDoRetorno,
    {
      readonly Icone: LucideIcon;
      /** O bloco do ícone, na tinta do tom (p.13). */
      readonly bloco: string;
      readonly icone: string;
      /** A barra de tempo, na cor do tom. */
      readonly barra: string;
      /** Dito antes do título, para quem ouve (nunca só a cor, p.20). */
      readonly falado: string;
    }
  >
> = {
  sucesso: { Icone: CircleCheck, bloco: 'bg-sucesso-fundo', icone: 'text-sucesso', barra: 'bg-sucesso', falado: 'Feito.' },
  ressalva: { Icone: TriangleAlert, bloco: 'bg-atencao-fundo', icone: 'text-atencao', barra: 'bg-atencao', falado: 'Feito, com ressalva.' },
  falha: { Icone: CircleX, bloco: 'bg-erro-fundo', icone: 'text-erro', barra: 'bg-erro', falado: 'Não foi feito.' },
  andamento: { Icone: CloudUpload, bloco: 'bg-informacao-fundo', icone: 'text-informacao', barra: 'bg-informacao', falado: 'Em andamento.' },
};

/** "ABS-004812 · 14:32 · João Silva" (p.32, 6). */
export function linhaDoRegistro(r: Retorno, pendente?: string): string {
  return [r.registro.codigo, r.registro.hora, pendente ?? r.registro.autor].filter(Boolean).join(' · ');
}

/** O efeito como frase, para quem lê o efeito em texto (o celular, o leitor de tela). */
export function efeitoEmTexto(efeito: EfeitoDoRetorno): string {
  if (typeof efeito === 'string') return efeito;
  if (efeito.antes !== undefined && efeito.depois !== undefined) return `${efeito.rotulo}: de ${efeito.antes} para ${efeito.depois}`;
  if (efeito.depois !== undefined) return `${efeito.rotulo}: ${efeito.depois}`;
  return efeito.rotulo;
}

/**
 * A contagem que pausa (p.32, 8): "Passar o mouse ou tocar pausa a contagem."
 *
 * `pointerenter`/`pointerleave` servem aos dois: o mouse que passa por cima e
 * o dedo que toca — no toque, o `pointerleave` vem quando o dedo sai. O foco
 * do teclado dentro do toast também pausa: quem chegou ao "Ver" pelo Tab não
 * pode ver o toast sumir debaixo dele. A barra de tempo pausa junto (`pausado`).
 */
export function usePausaDoRetorno(r: Retorno) {
  const [pausado, setPausado] = useState(false);
  const conta = r.duracaoMs !== null;
  useEffect(() => {
    if (!conta) return;
    if (pausado) pausarRetorno(r.id);
    else retomarRetorno(r.id);
  }, [pausado, conta, r.id]);
  const pausar = () => {
    setPausado(true);
  };
  const retomar = () => {
    setPausado(false);
  };
  return {
    pausado,
    eventos: conta
      ? {
          onPointerEnter: pausar,
          onPointerLeave: retomar,
          onFocus: pausar,
          onBlur: (evento: { readonly currentTarget: Element; readonly relatedTarget: Element | null }) => {
            if (!evento.currentTarget.contains(evento.relatedTarget)) retomar();
          },
        }
      : {},
  };
}

/**
 * A FALHA DE LEITURA E DA TELA (item 12 dos robos, 20/10/2026): trocar de tela
 * tira as falhas de leitura da tela que ficou para tras — elas nao somem
 * sozinhas, e empilhavam sobre o painel da tela seguinte. O retorno de
 * gravacao fica. Mesa (`Toasts`) e celular (`Retornos`) chamam o mesmo.
 */
export function useLeiturasSaemComATela(): void {
  const caminho = useRouterState({ select: (estado) => estado.location.pathname });
  useEffect(() => {
    trocouDeTela();
  }, [caminho]);
}
